import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { getRecentLogs } from '../../../common/frontend/utils/console-buffer.js'
import { t } from '../../../common/i18n.js'
import {
    FEEDBACK_ATTACHMENTS_MAX,
    FEEDBACK_IMAGE_ACCEPT,
    FEEDBACK_IMAGE_MIME_TYPES,
    FEEDBACK_TEXT_MAX,
} from '../../../common/models/feedback.js'
import { httpSend } from '../../http-utils.js'
import { View } from '../../types.js'
import { errorMessageFromBody } from '../../utils/api-error-message.js'
import { notify } from '../../utils/notification-utils.js'
import { Button } from '../button/button.js'
import { TextField } from '../forms/text-field/text-field.js'
import { Send, Trash2, Upload } from '../icon/solar-icons.js'
import { BaseModal } from '../modal/base-modal/base-modal.js'
import './feedback-widget.scss'

interface PreparedFile {
    file: File
    previewUrl: string
}

interface FeedbackWidgetProps {
    view: View
}

const isAcceptedImage = (type: string): type is (typeof FEEDBACK_IMAGE_MIME_TYPES)[number] =>
    (FEEDBACK_IMAGE_MIME_TYPES as readonly string[]).includes(type)

const uploadImage = async (view: View, file: File): Promise<string> => {
    const formData = new FormData()
    formData.append('image', file, file.name || 'screenshot')
    const res = await fetch('/api/upload/feedback-image', { method: 'POST', body: formData })
    if (!res.ok) {
        if (res.status === 401) {
            view.state.isAuthenticated = false
            view.state._onAuthChange?.()
        }
        // The endpoint emits a translatable `code` (FEEDBACK_*, UNAUTHORIZED, …);
        // the server message is the fallback, then the generic upload error.
        const body: unknown = await res.json().catch(() => null)
        const hasText = typeof body === 'object' && body !== null && Object.keys(body).length > 0
        throw new Error(
            hasText
                ? errorMessageFromBody(body, res.status, view.state.lang)
                : t.feedback.errorUploadFailed(res.status)(view.state.lang),
        )
    }
    const body = (await res.json()) as { imageUrl: string }
    return body.imageUrl
}

export const FeedbackWidget = (props: FeedbackWidgetProps) => {
    const { view } = props
    const lang = view.state.lang
    const [isOpen, setIsOpen] = useState(false)
    const [text, setText] = useState('')
    const [files, setFiles] = useState<PreparedFile[]>([])
    const [error, setError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const reset = () => {
        files.forEach((f) => URL.revokeObjectURL(f.previewUrl))
        setText('')
        setFiles([])
        setError(null)
        setIsSubmitting(false)
    }

    const close = () => {
        if (isSubmitting) return
        setIsOpen(false)
        reset()
    }

    const addFiles = (incoming: File[]) => {
        if (incoming.length === 0) return
        setFiles((prev) => {
            const next = [...prev]
            for (const file of incoming) {
                if (!isAcceptedImage(file.type)) continue
                if (next.length >= FEEDBACK_ATTACHMENTS_MAX) break
                next.push({ file, previewUrl: URL.createObjectURL(file) })
            }
            return next
        })
    }

    const onPickFiles = (event: ChangeEvent<HTMLInputElement>) => {
        addFiles(Array.from(event.target.files ?? []))
        event.target.value = ''
    }

    const removeFile = (index: number) => {
        URL.revokeObjectURL(files[index].previewUrl)
        setFiles(files.filter((_, i) => i !== index))
    }

    // Paste-to-attach: while the modal is open, intercept image pastes
    // anywhere in the document and add them as screenshot attachments.
    useEffect(() => {
        if (!isOpen) return

        const onPaste = (event: ClipboardEvent) => {
            const items = event.clipboardData?.items
            if (!items) return

            const pastedFiles: File[] = []
            for (let i = 0; i < items.length; i++) {
                const item = items[i]
                if (item.kind !== 'file') continue
                const file = item.getAsFile()
                if (file && isAcceptedImage(file.type)) pastedFiles.push(file)
            }

            if (pastedFiles.length > 0) {
                event.preventDefault()
                addFiles(pastedFiles)
            }
        }

        document.addEventListener('paste', onPaste)
        return () => document.removeEventListener('paste', onPaste)
    }, [isOpen])

    const submit = async () => {
        const trimmed = text.trim()
        if (!trimmed) {
            setError(t.feedback.errorRequired(lang))
            return
        }
        if (trimmed.length > FEEDBACK_TEXT_MAX) {
            setError(t.feedback.errorTooLong(FEEDBACK_TEXT_MAX)(lang))
            return
        }
        if (files.length > FEEDBACK_ATTACHMENTS_MAX) {
            setError(t.feedback.errorTooManyAttachments(FEEDBACK_ATTACHMENTS_MAX)(lang))
            return
        }

        setIsSubmitting(true)
        setError(null)

        try {
            const imageUrls: string[] = []
            for (const { file } of files) {
                const url = await uploadImage(view, file)
                imageUrls.push(url)
            }

            await httpSend(view.state, 'POST', '/feedback', {
                text: trimmed,
                image_urls: imageUrls,
                url: window.location.href,
                user_agent: navigator.userAgent,
                viewport_width: window.innerWidth,
                viewport_height: window.innerHeight,
                device_pixel_ratio: window.devicePixelRatio,
                console_logs: getRecentLogs(),
            })

            notify({
                view,
                type: 'success',
                text: t.feedback.successToast(lang),
            })
            setIsOpen(false)
            reset()
        } catch (err) {
            setError(err instanceof Error ? err.message : t.feedback.errorGeneric(lang))
            setIsSubmitting(false)
        }
    }

    useEffect(() => {
        view.state._openFeedback = () => setIsOpen(true)
        return () => {
            delete view.state._openFeedback
        }
    }, [view])

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={close}
            disableBackdropClose={isSubmitting}
            className="feedback-widget__modal"
            showClose
            closeAriaLabel={t.feedback.closeAriaLabel(lang)}
        >
            <div className="feedback-widget__header">
                <h2 className="feedback-widget__title">{t.feedback.title(lang)}</h2>
            </div>
            <p className="feedback-widget__hint">{t.feedback.hint(lang)}</p>
            <TextField
                as="textarea"
                label={t.feedback.messageLabel(lang)}
                isRequired
                value={text}
                onChange={setText}
                placeholder={t.feedback.messagePlaceholder(lang)}
                textAreaProps={{ rows: 6, maxLength: FEEDBACK_TEXT_MAX }}
            />

            <div className="feedback-widget__files">
                <div className="feedback-widget__files-header">
                    <span className="feedback-widget__files-label">
                        {t.feedback.screenshotsLabel(files.length)(lang)}
                    </span>
                    <Button
                        text={t.feedback.addScreenshot(lang)}
                        iconLeft={Upload}
                        variant="secondary"
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        isDisabled={isSubmitting || files.length >= FEEDBACK_ATTACHMENTS_MAX}
                    />
                </div>
                <span className="feedback-widget__paste-hint">{t.feedback.pasteHint(lang)}</span>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={FEEDBACK_IMAGE_ACCEPT}
                    multiple
                    className="feedback-widget__file-input"
                    onChange={onPickFiles}
                />
                {files.length > 0 && (
                    <ul className="feedback-widget__file-list">
                        {files.map((f, index) => (
                            <li className="feedback-widget__file-item" key={f.previewUrl}>
                                <img
                                    src={f.previewUrl}
                                    alt=""
                                    className="feedback-widget__file-thumb"
                                />
                                <span className="feedback-widget__file-name">
                                    {f.file.name || t.common.screenshotName(index + 1)(lang)}
                                </span>
                                <button
                                    type="button"
                                    className="feedback-widget__file-remove"
                                    onClick={() => removeFile(index)}
                                    aria-label={t.feedback.removeAttachmentAriaLabel(
                                        f.file.name || t.common.screenshotName(index + 1)(lang),
                                    )(lang)}
                                    disabled={isSubmitting}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {error && <div className="feedback-widget__error">{error}</div>}

            <div className="feedback-widget__actions">
                <Button
                    text={t.feedback.cancel(lang)}
                    variant="tertiary"
                    size="small"
                    onClick={close}
                    isDisabled={isSubmitting}
                />
                <Button
                    text={isSubmitting ? t.feedback.sending(lang) : t.feedback.send(lang)}
                    iconLeft={Send}
                    variant="primary"
                    size="small"
                    onClick={submit}
                    isDisabled={isSubmitting}
                />
            </div>
        </BaseModal>
    )
}
